/**
 * Shared SVG icons — single source so per-route files don't each define
 * their own ChevronL / CloseIcon / etc. All icons accept a `size` prop
 * and inherit `currentColor` for stroke, so callers drive color via Tailwind
 * classes on the parent.
 */
import type { SVGProps } from 'react';

interface IconProps extends SVGProps<SVGSVGElement> {
  size?: number;
}

const base = (size: number, rest: SVGProps<SVGSVGElement>) => ({
  viewBox: '0 0 24 24',
  fill: 'none' as const,
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  width: size,
  height: size,
  ...rest,
});

export const ChevronLeft  = ({ size = 12, ...p }: IconProps) => (<svg {...base(size, p)}><polyline points="15 18 9 12 15 6" /></svg>);
export const ChevronRight = ({ size = 12, ...p }: IconProps) => (<svg {...base(size, p)}><polyline points="9 18 15 12 9 6" /></svg>);
export const ChevronDown  = ({ size = 11, ...p }: IconProps) => (<svg {...base(size, p)}><polyline points="6 9 12 15 18 9" /></svg>);

export const Close = ({ size = 18, ...p }: IconProps) => (
  <svg {...base(size, p)}>
    <line x1="18" y1="6"  x2="6"  y2="18" />
    <line x1="6"  y1="6"  x2="18" y2="18" />
  </svg>
);

export const Menu = ({ size = 18, ...p }: IconProps) => (
  <svg {...base(size, p)}>
    <line x1="3" y1="6"  x2="21" y2="6" />
    <line x1="3" y1="12" x2="21" y2="12" />
    <line x1="3" y1="18" x2="21" y2="18" />
  </svg>
);

export const Back = ({ size = 14, ...p }: IconProps) => (
  <svg {...base(size, p)}>
    <line x1="19" y1="12" x2="5" y2="12" />
    <polyline points="12 19 5 12 12 5" />
  </svg>
);

export const Search = ({ size = 14, ...p }: IconProps) => (
  <svg {...base(size, p)}>
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

export const Check = ({ size = 12, ...p }: IconProps) => (
  <svg {...base(size, p)}>
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

export const Filter = ({ size = 13, ...p }: IconProps) => (
  <svg {...base(size, { ...p, strokeWidth: 1.75 })}>
    <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
  </svg>
);

export const ExternalLink = ({ size = 10, ...p }: IconProps) => (
  <svg {...base(size, p)}>
    <path d="M7 17 17 7M7 7h10v10" />
  </svg>
);

export const User = ({ size = 14, ...p }: IconProps) => (
  <svg {...base(size, p)}>
    <circle cx="12" cy="7" r="4" />
    <path d="M4 21v-1a6 6 0 0 1 6-6h4a6 6 0 0 1 6 6v1" />
  </svg>
);

export const Lock = ({ size = 14, ...p }: IconProps) => (
  <svg {...base(size, p)}>
    <rect x="4" y="11" width="16" height="10" rx="2" />
    <path d="M8 11V7a4 4 0 0 1 8 0v4" />
  </svg>
);

export const Eye = ({ size = 14, ...p }: IconProps) => (
  <svg {...base(size, p)}>
    <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

export const EyeOff = ({ size = 14, ...p }: IconProps) => (
  <svg {...base(size, p)}>
    <path d="M17.94 17.94A10.97 10.97 0 0 1 12 19c-6.5 0-10-7-10-7a18.6 18.6 0 0 1 4.06-5.06" />
    <path d="M9.9 4.24A10.93 10.93 0 0 1 12 4c6.5 0 10 7 10 7a18.5 18.5 0 0 1-2.16 3.19" />
    <path d="M14.12 14.12A3 3 0 1 1 9.88 9.88" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
);

export const LogOut = ({ size = 14, ...p }: IconProps) => (
  <svg {...base(size, p)}>
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
    <polyline points="16 17 21 12 16 7" />
    <line x1="21" y1="12" x2="9" y2="12" />
  </svg>
);

export const Star = ({ size = 12, ...p }: IconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" width={size} height={size} {...p}>
    <path d="M12 2l2.9 6.5 7.1.6-5.4 4.6 1.7 6.9L12 16.9l-6.3 3.7 1.7-6.9L2 9.1l7.1-.6L12 2z" />
  </svg>
);
